/**
 * SUNTRONIX ONE — Renewal digest sending calendar.
 *
 * GitHub-style contribution heatmap that shows which weeks the weekly
 * renewal digest actually went out — so IT can audit "did Monday's
 * email really fire?" without digging through logs.
 *
 * Each cell = 1 day.  Colour intensity scales with the number of
 * delivered digests on that day.  Failed days are ringed red.  Click
 * a cell to see the digests that ran on that day (id, subject,
 * recipients, tenant, status).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Mail, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DOW_LABELS = ["Mon", "", "Wed", "", "Fri", "", ""]; // sparse labels
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Return array of ISO YYYY-MM-DD strings for every day in `year`,
// starting from the first Monday on or before Jan 1 so the grid aligns
// to whole weeks (7 rows × ~53 cols).
function buildGridDays(year) {
  const jan1 = new Date(Date.UTC(year, 0, 1));
  // JS day: 0=Sun .. 6=Sat.  We want Mon=0 .. Sun=6.
  const jsDow = jan1.getUTCDay();
  const monIdx = (jsDow + 6) % 7; // Mon → 0
  const start = new Date(jan1);
  start.setUTCDate(start.getUTCDate() - monIdx);
  const days = [];
  const end = new Date(Date.UTC(year + 1, 0, 1));
  // Extend to complete the last week
  const endJs = end.getUTCDay();
  const endMonIdx = (endJs + 6) % 7;
  const cutoff = new Date(end);
  cutoff.setUTCDate(cutoff.getUTCDate() + (endMonIdx === 0 ? 0 : 7 - endMonIdx));
  const cur = new Date(start);
  while (cur < cutoff) {
    days.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
}

function intensity(sent) {
  if (sent <= 0) return 0;
  if (sent <= 1) return 1;
  if (sent <= 3) return 2;
  if (sent <= 7) return 3;
  return 4;
}
const TONE = [
  "bg-slate-100 border-slate-200",   // 0
  "bg-emerald-100 border-emerald-200",
  "bg-emerald-300 border-emerald-400",
  "bg-emerald-500 border-emerald-600",
  "bg-emerald-700 border-emerald-800",
];

export default function DigestSendingCalendar({ masterCompanyId, canPickTenant = false, testId = "digest-calendar" }) {
  const [year, setYear] = useState(() => new Date().getUTCFullYear());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openDay, setOpenDay] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/renewals/digest/calendar", {
        params: { year, master_company_id: masterCompanyId || undefined },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sending log");
    } finally { setBusy(false); }
  }, [year, masterCompanyId]);

  useEffect(() => { load(); }, [load]);

  const cellsByDate = useMemo(() => {
    const m = {};
    for (const c of data?.cells || []) m[c.date] = c;
    return m;
  }, [data]);

  const days = useMemo(() => buildGridDays(year), [year]);
  const inYear = useCallback((iso) => iso.startsWith(String(year)), [year]);

  // Break days into weeks of 7 (Mon..Sun)
  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < days.length; i += 7) w.push(days.slice(i, i + 7));
    return w;
  }, [days]);

  // Compute month label positions (top row: label at first week whose Monday is in that month)
  const monthLabels = useMemo(() => {
    const seen = new Set();
    return weeks.map((wk) => {
      const first = wk[0];
      if (!first || !inYear(first)) return "";
      const m = parseInt(first.slice(5, 7), 10) - 1;
      if (seen.has(m)) return "";
      seen.add(m);
      return MONTH_LABELS[m];
    });
  }, [inYear, weeks]);

  const totals = data?.totals || { runs: 0, sent: 0, failed: 0, skipped: 0, alerts: 0 };
  const activeDayCell = openDay ? cellsByDate[openDay] : null;

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4" data-testid={testId}>
      <header className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center">
          <CalendarDays className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Digest sending log</div>
          <h3 className="text-[14px] font-semibold text-slate-900 truncate">
            Renewal digest history · {year}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          data-testid={`${testId}-prev-year`}
          className="h-7 w-7 rounded border border-slate-200 grid place-items-center text-slate-500 hover:bg-slate-50"
          title="Previous year"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-[12px] font-medium text-slate-900 w-12 text-center">{year}</div>
        <button
          type="button"
          onClick={() => setYear((y) => y + 1)}
          data-testid={`${testId}-next-year`}
          className="h-7 w-7 rounded border border-slate-200 grid place-items-center text-slate-500 hover:bg-slate-50"
          title="Next year"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </header>

      {/* Totals strip */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <Kpi label="Runs"     value={totals.runs}     tone="slate" />
        <Kpi label="Sent"     value={totals.sent}     tone="emerald" />
        <Kpi label="Failed"   value={totals.failed}   tone="rose" />
        <Kpi label="Alerts"   value={totals.alerts}   tone="amber" />
      </div>

      {/* Grid */}
      <div className="relative overflow-x-auto">
        {busy && (
          <div className="absolute inset-0 z-10 grid place-items-center bg-white/70">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        )}
        <div className="inline-flex flex-col gap-1 min-w-max">
          {/* Month labels */}
          <div className="flex gap-[3px] pl-6">
            {weeks.map((_, wIdx) => (
              <div key={wIdx} className="w-[11px] text-[9px] text-slate-500 tracking-tight">
                {monthLabels[wIdx]}
              </div>
            ))}
          </div>
          {/* Rows: 7 (Mon..Sun) */}
          {[0, 1, 2, 3, 4, 5, 6].map((row) => (
            <div key={row} className="flex items-center gap-[3px]">
              <div className="w-5 text-right text-[9px] text-slate-500 pr-1">{DOW_LABELS[row]}</div>
              {weeks.map((wk, wIdx) => {
                const iso = wk[row];
                if (!iso || !inYear(iso)) {
                  return <div key={wIdx} className="w-[11px] h-[11px] rounded-[2px] opacity-0" />;
                }
                const cell = cellsByDate[iso];
                const sent = cell?.sent || 0;
                const failed = cell?.failed || 0;
                const tone = TONE[intensity(sent)];
                const hasAny = cell?.runs > 0;
                const isSelected = openDay === iso;
                const label = hasAny
                  ? `${iso} · ${sent} sent${failed ? ` · ${failed} failed` : ""}`
                  : `${iso} · no runs`;
                return (
                  <button
                    key={wIdx}
                    type="button"
                    title={label}
                    aria-label={label}
                    onClick={() => setOpenDay(hasAny ? iso : null)}
                    data-testid={`${testId}-cell-${iso}`}
                    className={
                      "w-[11px] h-[11px] rounded-[2px] border transition-transform " +
                      tone +
                      (failed > 0 ? " ring-1 ring-rose-500 ring-offset-0" : "") +
                      (isSelected ? " scale-125 outline outline-2 outline-slate-900" : " hover:scale-110")
                    }
                  />
                );
              })}
            </div>
          ))}
          {/* Legend */}
          <div className="flex items-center gap-1.5 mt-2 pl-6 text-[10px] text-slate-500">
            <span>Less</span>
            {TONE.map((t, i) => (
              <div key={i} className={"w-[11px] h-[11px] rounded-[2px] border " + t} />
            ))}
            <span>More</span>
            <div className="w-3" />
            <div className="w-[11px] h-[11px] rounded-[2px] border bg-slate-100 border-slate-200 ring-1 ring-rose-500" />
            <span>Failed</span>
          </div>
        </div>
      </div>

      {activeDayCell && (
        <DaySheet
          cell={activeDayCell}
          canPickTenant={canPickTenant}
          onClose={() => setOpenDay(null)}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const tones = {
    slate:   "text-slate-700 bg-slate-50 border-slate-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose:    "text-rose-700 bg-rose-50 border-rose-200",
    amber:   "text-amber-700 bg-amber-50 border-amber-200",
  };
  return (
    <div className={"rounded border px-2.5 py-1.5 " + (tones[tone] || tones.slate)}>
      <div className="text-[9px] uppercase tracking-[0.14em] opacity-80">{label}</div>
      <div className="text-[16px] font-semibold">{value}</div>
    </div>
  );
}

function DaySheet({ cell, canPickTenant, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex justify-end" onClick={onClose}
         data-testid="digest-day-sheet">
      <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Digest sends</div>
            <div className="text-[14px] font-semibold text-slate-900">{cell.date}</div>
          </div>
          <button onClick={onClose} data-testid="digest-day-close" className="h-8 w-8 rounded grid place-items-center text-slate-500 hover:bg-slate-50">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-4 gap-2 text-center">
            <MiniStat label="Runs"    value={cell.runs}    />
            <MiniStat label="Sent"    value={cell.sent}    tone="emerald" />
            <MiniStat label="Failed"  value={cell.failed}  tone="rose" />
            <MiniStat label="Alerts"  value={cell.alerts}  tone="amber" />
          </div>
          <div className="space-y-2">
            {cell.digests.map((d) => (
              <div key={d.id} className="rounded border border-slate-200 p-3">
                <div className="flex items-start gap-2">
                  <div className={"h-6 w-6 rounded grid place-items-center shrink-0 " +
                    (d.status === "SENT" ? "bg-emerald-100 text-emerald-700"
                    : d.status === "FAILED" ? "bg-rose-100 text-rose-700"
                    : "bg-slate-100 text-slate-500")}>
                    <Mail className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-slate-900 truncate">{d.subject || "—"}</div>
                    {canPickTenant && d.master_company_name && (
                      <div className="text-[10px] text-slate-500">{d.master_company_name}</div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {d.alert_count} alert{d.alert_count === 1 ? "" : "s"} · {d.recipients.length} recipient{d.recipients.length === 1 ? "" : "s"}
                    </div>
                    {d.recipients.length > 0 && (
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate">{d.recipients.join(", ")}</div>
                    )}
                  </div>
                  <span className={"text-[10px] uppercase tracking-[0.14em] shrink-0 " +
                    (d.status === "SENT" ? "text-emerald-700"
                    : d.status === "FAILED" ? "text-rose-700"
                    : "text-slate-500")}>{d.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const tones = {
    default: "text-slate-800",
    emerald: "text-emerald-700",
    rose:    "text-rose-700",
    amber:   "text-amber-700",
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={"text-[15px] font-semibold " + (tones[tone] || tones.default)}>{value}</div>
    </div>
  );
}
